import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Reply as ReplyIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  PriorityHigh as PriorityHighIcon,
} from '@mui/icons-material';
import { messageService } from '../../services/messageService';
import { FrontendSMS } from '../../types/sms';
import { useTheme } from '@mui/material/styles';

interface HireConversation {
  hire: {
    id: string;
    fullName: string;
    mobileNumber: string;
    email: string;
  };
  messages: FrontendSMS[];
  latestMessageAt: string;
}

const HireConversationHistory: React.FC = () => {
  const [conversations, setConversations] = useState<HireConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const theme = useTheme();

  // Chat dialog state
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<HireConversation | null>(null);
  const [chatMessages, setChatMessages] = useState<FrontendSMS[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingSingle, setSendingSingle] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await messageService.getHireConversations();
      setConversations(data);
    } catch (error: any) {
      console.error('Error fetching hire conversations:', error);
      showSnackbar('Error loading conversations: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessagesForHire = async (hireId: string) => {
    try {
      setMessagesLoading(true);
      const msgs = await messageService.getMessagesForHire(hireId);
      setChatMessages(msgs);
    } catch (err: any) {
      console.error('Error fetching messages for hire:', err);
      showSnackbar('Error loading messages: ' + err.message, 'error');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleOpenChat = (convo: HireConversation) => {
    setSelectedConversation(convo);
    setChatOpen(true);
    setNewMessage('');
    fetchMessagesForHire(convo.hire.id);
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setSelectedConversation(null);
    setChatMessages([]);
    setNewMessage('');
  };

  const handleSendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;
    const text = newMessage.trim();
    setNewMessage('');
    try {
      setSendingSingle(true);
      const res = await messageService.sendHireSMS({
        hireId: selectedConversation.hire.id,
        phoneNumber: selectedConversation.hire.mobileNumber,
        message: text,
      });
      if (res.success) {
        showSnackbar('Message sent successfully', 'success');
        await fetchMessagesForHire(selectedConversation.hire.id);
        // Refresh the conversation list too so latest timestamps update
        fetchConversations();
      } else {
        showSnackbar(res.message || 'Failed to send message', 'error');
      }
    } catch (err: any) {
      showSnackbar('Error sending message: ' + err.message, 'error');
    } finally {
      setSendingSingle(false);
    }
  };

  const getDirectionIcon = (direction: string) =>
    direction === 'inbound' ? <ReplyIcon fontSize="small" /> : <SendIcon fontSize="small" />;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredConversations = conversations.filter((convo) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      convo.hire.fullName.toLowerCase().includes(q) ||
      convo.hire.mobileNumber.toLowerCase().includes(q) ||
      convo.hire.email.toLowerCase().includes(q) ||
      convo.messages.some((m) => m.message.toLowerCase().includes(q))
    );
  });

  const totalConversations = conversations.length;
  const displayedConversations = showAll
    ? filteredConversations
    : filteredConversations.slice(0, 100);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and Refresh */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchConversations}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? 'Show First 100' : 'Show All'}
        </Button>
        <Typography variant="body2" color="textSecondary">
          Total conversations: {totalConversations}
          {filteredConversations.length !== totalConversations &&
            ` • Matching filters: ${filteredConversations.length}`}
        </Typography>
      </Box>

      {/* Conversation list */}
      {displayedConversations.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {search ? 'No conversations match your search' : 'No hire conversations yet'}
          </Typography>
        </Paper>
      ) : (
        <List disablePadding>
          {displayedConversations.map((convo, index) => {
            const latestMsg = convo.messages[0];
            const hasInboundLatest = latestMsg && latestMsg.direction !== 'outbound';
            return (
              <React.Fragment key={convo.hire.id}>
                {index > 0 && <Divider />}
                <ListItem
                  component={Paper}
                  elevation={1}
                  sx={{
                    mb: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                  onClick={() => handleOpenChat(convo)}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {convo.hire.fullName}
                        </Typography>
                        {hasInboundLatest && (
                          <Tooltip title="Latest message is from the hire">
                            <PriorityHighIcon
                              sx={{ fontSize: 18, color: theme.palette.warning.main }}
                            />
                          </Tooltip>
                        )}
                        <Chip
                          icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                          label={convo.hire.mobileNumber}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={`${convo.messages.length} message${convo.messages.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      latestMsg ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '500px',
                            }}
                          >
                            {latestMsg.direction === 'outbound' ? 'You: ' : ''}
                            {latestMsg.message}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(latestMsg.created_at)}
                          </Typography>
                        </Box>
                      ) : null
                    }
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}

      {/* Chat Dialog */}
      <Dialog
        open={chatOpen}
        onClose={handleCloseChat}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedConversation?.hire.fullName}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {selectedConversation?.hire.email} · {selectedConversation?.hire.mobileNumber}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseChat}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {messagesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {chatMessages.length === 0 ? (
                  <Typography color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                    No messages yet. Send one below.
                  </Typography>
                ) : (
                  [...chatMessages].reverse().map((msg) => (
                    <ListItem
                      key={msg.id}
                      sx={{
                        flexDirection: msg.direction === 'outbound' ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemAvatar sx={{ order: msg.direction === 'outbound' ? 2 : 0 }}>
                        <Avatar
                          sx={{
                            bgcolor: msg.direction === 'outbound'
                              ? theme.palette.secondary.main
                              : theme.palette.primary.main,
                            width: 32,
                            height: 32,
                          }}
                        >
                          {getDirectionIcon(msg.direction)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ order: 1, textAlign: msg.direction === 'outbound' ? 'right' : 'left' }}
                        primary={
                          <Box
                            sx={{
                              backgroundColor: msg.direction === 'outbound'
                                ? theme.palette.primary.main
                                : theme.palette.grey[300],
                              color: msg.direction === 'outbound' ? 'white' : theme.palette.text.primary,
                              p: 2,
                              borderRadius: 2,
                              maxWidth: '70%',
                              display: 'inline-block',
                              textAlign: 'left',
                            }}
                          >
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.message}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                              {formatDate(msg.created_at)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Box>
          {/* Compose */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={3}
                disabled={sendingSingle}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendingSingle}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                {sendingSingle ? <CircularProgress size={20} /> : <SendIcon />}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HireConversationHistory;
